<template>
    <div class="w-100 h-100 position-relative">
        <div :id="cMapId" class="map-component" :class="{'static-map': staticMap, active: activeMapOnLocation, 'overlay': showInfoBox }" tabindex="0"></div>
        <div v-if="staticMap" class="map-overlay" :class="((x > 0 || y > 0) && !focused) ? 'visible' : ''">
            <i class="far fa-hand-pointer fa-4x" />
            <h4>Klicka för att interagera med kartan</h4>
        </div>
        <div :id="`popup-${cMapId}`" class="ol-popup">
            <a href="#" :id="`popup-closer-${cMapId}`" class="ol-popup-closer" @click.prevent="hidePopup"></a>
            <div id="popup-content" v-if="popup" class="d-flex flex-column">
                <h5 id="popup-title">{{ popup.title }}</h5>
                <h6 id="popup-subtitle" v-if="popup.subtitle">{{ popup.subtitle }}</h6>
                <p v-if="popup.desc">{{ popup.desc }}</p>
                <a id="popup-link" :href="popup.link || '#'"
                   v-if="popupShouldShowLink()"
                   v-b-modal.bygginv-modal>Visa information</a>
                <a href="#" id="popup-zoom" v-if="popup.extent" @click="handleZoomInFeature(popup.feature)">Zooma
                    in</a>
            </div>
        </div>
        <div v-if="popup && popup.modal">
            <byggnadsinv-modal :popup="popup"></byggnadsinv-modal>
        </div>

        <div class="toggle-byggnadsinverntering-containter" v-if="!staticMap && !isArticlePage">
            <div class="toggle-btn " :class="{'overlay': showInfoBox }">
              <label>Byggnadsinventeringar</label>
              <i tabindex="0" @keydown.enter="showInfoBox = !showInfoBox" @mouseover="showInfoBox = true" @mouseleave="showInfoBox = false" class="fa fa-info-circle"></i>
                <input v-model="showByggnadsInventering" type="checkbox" id="switch"/>
                <div tabindex="0" for="switch" @click="toggleBuildings()" @keydown.enter="toggleBuildings()"></div>
            </div>
            <div class="toggle-info" v-if="showInfoBox">
            <p>Byggnadsinventeringar är gjorda i samarbete mellan Länsstyrelsen i Västmanlands län, Västmanlands läns museum och respektive kommun.</p>
            <p>Syftet är att uppmärksamma och sprida kunskap om fysiska miljöer och byggnader.</p>
        </div>
      </div>

    </div>
</template>

<script>
/* global Bus */
import Map from 'ol/Map'
import View from 'ol/View'
import Overlay from 'ol/Overlay'
import { defaults as defaultControls } from 'ol/control'
import { fromLonLat } from 'ol/proj'
import { defaults as defaultInteractions } from 'ol/interaction'
import GeoJSON from 'ol/format/GeoJSON'
import * as olExtent from 'ol/extent'
import * as turf from '@turf/turf'
import { getCoord } from '@turf/invariant'
import currentLocationLayer from './layers/currentLocationLayer'
import markerLayer from './layers/markerLayer'
import vastmanlandLayer from './layers/vastmanlandLayer'
import kulturarvOmraden from './layers/kulturarvOmradenLayer'
import wmtsLayer from './layers/wmtsLayer'
import { getTypesForLayers, isCluster, setupMarkers } from './helpers'
import kulturarvByggnader from './layers/kulturarvByggnaderLayer'
import kulturarvStadsdelar from './layers/kulturarvStadsdelarLayer'
import testLayer from './layers/testLayer'
import geoLocationLayer from './layers/geoLocationLayer'
import ByggnadsinvModal from '../byggnadsinv-modal/ByggnadsinvModal'
import { mapState } from 'vuex'
import { getUrl, setupGeoJson } from '../../helpers'
import store from '../../store'
import 'url-search-params-polyfill'

var _scrollTimeout = null

export default {
  components: { ByggnadsinvModal },
  props: ['mapId', 'markers', 'staticMap', 'geoJson', 'currentLocation', 'inactiveMap', 'type'],

  data () {
    return {
      map: null,
      tileLayer: null,
      markerLayer: null,
      markerLayerStyleCache: {},
      currentLocationLayer: currentLocationLayer(this.currentLocation),
      vastmanlandLayer: null,
      geoLocationLayer: null,
      testLayer: testLayer(),
      popupOverlay: null,
      currentExtent: this.savedViewExtent,
      popup: null,
      zoomedInMunicipality: null,
      onByggnadsInventering: _.includes(window.location.pathname, 'byggnadsinventering'),
      isArticlePage: _.includes(window.location.pathname, 'artikel'),
      byggnadsInvLayers: [],
      baseUrl: process.env.MIX_ASSET_URL,
      mounted: false,
      focused: false,
      dragging: false,
      showInfoBox: false,
      x: 0,
      y: 0
    }
  },

  async mounted () {
    await store.restored

    this.$store.dispatch('setActiveMapOnLocation', false)

    this.setupMap().then(() => {
      this.geoLocationLayer = geoLocationLayer(this.map.getView().getProjection(), pos => this.$store.dispatch('setGeoLocation', pos))
      this.map.addLayer(this.geoLocationLayer)
      this.currentExtent = this.savedViewExtent

      this.$nextTick(() => {
        if (this.staticMap) {
          this.fitMapToExtent(this.getExtentIfFinite(this.currentLocationLayer.getSource().getExtent()))
        } else {
          this.refreshMap()
        }

        this.mounted = true
      })
    })

    if (_.includes(window.location.href, 'municipality')) {
      const searchParams = new URLSearchParams(window.location.search)
      const municipality = searchParams.get('municipality')
      searchParams.delete('municipality')
      window.history.replaceState(null, null, `?${searchParams.toString()}`)

      const _this = this
      setTimeout(function () {
        const feature = _this.vastmanlandLayer.getSource().getFeatureById(municipality)
        const extent = feature.getGeometry().getExtent()
        _this.fitMapToExtent(extent)
      }, 100)
    }

    Bus.$on('nearMe', () => {
      const feature = this.geoLocationLayer.getSource().getFeatureById('positionFeature')
      this.handleNearMe(feature)
    })

    Bus.$on('zoomInVastmanland', () => {
      this.fitMapToExtent(this.getExtentIfFinite(this.vastmanlandLayer.getSource().getExtent()) || [1716795.5697331834, 8223282.213111257, 1888640.758976853, 8443292.172852054])
    })

    const mainEl = document.getElementsByTagName('main')[0]

    mainEl.addEventListener('mouseup', this.stopDrag)
    mainEl.addEventListener('mousedown', this.startDrag)
    mainEl.addEventListener('mousemove', this.doDrag)
    mainEl.addEventListener('mousewheel', this.handleScroll)
    mainEl.addEventListener('DOMMouseScroll', this.handleScroll)
  },

  destroyed () {
    const mainEl = document.getElementsByTagName('main')[0]
    mainEl.removeEventListener('mouseup', this.stopDrag)
    mainEl.removeEventListener('mousedown', this.startDrag)
    mainEl.removeEventListener('mousemove', this.doDrag)
    mainEl.removeEventListener('mousewheel', this.handleScroll)
    mainEl.removeEventListener('DOMMouseScroll', this.handleScroll)
  },

  methods: {
    toggleBuildings: function () {
      store.commit('setTogglebyggnadsinventering', !this.showByggnadsInventering)
    },
    handleScroll (e) {
      const bgMapWrapper = document.getElementById('location-bg-map-wrapper')
      const contentWrapper = _.first(document.getElementsByClassName('content-wrapper'))
      const contentWrapperIsHidden = contentWrapper ? contentWrapper.classList.contains('hidden') : false

      if (!bgMapWrapper || contentWrapperIsHidden) {
        return
      }

      clearTimeout(_scrollTimeout)
      _scrollTimeout = setTimeout(function () {
        bgMapWrapper.classList.remove('pointer-events-none')
      }, 250)
      bgMapWrapper.classList.add('pointer-events-none')
    },
    startDrag () {
      this.dragging = true
      this.x = this.y = null
    },
    stopDrag () {
      this.dragging = false
      this.x = this.y = 0
    },
    doDrag (event) {
      if (this.dragging) {
        this.x = event.clientX
        this.y = event.clientY
      }
    },
    handleMarkerLayer: function (data) {
      this.$nextTick(function () {
        if (this.markerLayer) {
          this.markerLayer.getSource().getSource().clear()
          this.markerLayer.getSource().getSource().addFeatures(new GeoJSON().readFeatures(data))
          this.setupPopup()
        }
      })
    },
    popupShouldShowLink: function () {
      return this.popup.link ||
          _.get(this.popup.modal, 'properties.beskrivning') ||
          _.get(this.popup.modal, 'properties.allman_info') ||
          _.get(this.popup.modal, 'properties.vardebeskrivning')
    },
    handleZoomInFeature: function (feature) {
      this.zoomedInMunicipality = feature
      this.currentExtent = feature && feature.getGeometry().getExtent()
    },
    refreshMap: function (fitMap = true) {
      this.hidePopup()
      const _this = this
      const minResolution = _.includes(window.location.pathname, 'byggnader_')
        ? 0.5
        : _.includes(window.location.pathname, 'omraden_')
          ? 4
          : 0.5
      setTimeout(function () {
        _this.map.updateSize()
        fitMap && _this.fitMapToExtent(this.savedViewExtent, 250, minResolution)
        _this.setupPopup()
        _this.visibleFeaturesInExtent(this.currentExtent)
      }, 100)
    },
    setupKulturarv: function () {
      if (this.showByggnadsInventering === false) {
        this.byggnadsInvLayers.forEach(layer => {
          this.map.removeLayer(layer)
        })

        this.byggnadsInvLayers = []

        return
      }

      const byggnadsInvIds = _.isEmpty(this.searchQuery) ? null : _.map(_.get(this.searchResults, 'byggnadsInventering.result'), item => ("'" + item.id + "'"))
      if (byggnadsInvIds && byggnadsInvIds.length === 0) {
        byggnadsInvIds.push(["''"])
      }

      getTypesForLayers().then(types => {
        _.each(types, type => {
          const layerName = `${_.camelCase(type)}Layer`
          this[layerName] = _.includes(type, 'stadsdelar')
            ? kulturarvStadsdelar({ type, byggnadsInvIds })
            : _.includes(type, 'omraden')
              ? kulturarvOmraden({ type, byggnadsInvIds })
              : kulturarvByggnader({ type, byggnadsInvIds })

          // if ((window.env === 'production' && !_.includes(type, 'sala')) || window.env !== 'production') {
          //   this.map.addLayer(this[layerName])
          // }
          this.byggnadsInvLayers.push(this[layerName])
          this.map.addLayer(this[layerName])
        })
      })
    },
    setupMap: function () {
      return new Promise(resolve => {
        this.setupKulturarv()
        const vastmanlandWebMercator = fromLonLat([15.6213095, 59.7022645])
        this.map = new Map({
          interactions: defaultInteractions({
            onFocusOnly: !!this.staticMap
          }),
          controls: defaultControls(),
          target: this.cMapId,
          layers: [this.currentLocationLayer],
          view: new View({
            center: vastmanlandWebMercator,
            zoom: 8
          })
        })

        this.vastmanlandLayer = vastmanlandLayer()
        this.map.addLayer(this.vastmanlandLayer)

        this.markerLayer = markerLayer(this.geoJson, this.currentLocation, this.markerLayerStyleCache)
        this.map.addLayer(this.markerLayer)

        wmtsLayer().then(tileLayer => {
          this.map.addLayer(tileLayer)
        })

        if (this.type !== 'search') {
          this.vastmanlandLayer.setVisible(false)
          this.markerLayer.setMaxResolution(99999)
        }

        // !this.staticMap && this.setupPopup()
        this.setupPopup()

        const onMoveStart = evt => {
          // this.focused = true
        }
        const onMoveEnd = (evt) => {
          const map = evt.map
          const viewExtent = map.getView().calculateExtent()
          this.visibleFeaturesInExtent(viewExtent)
          this.type === 'search' && this.mounted && this.$store.dispatch('setSavedViewExtent', {
            [this.type]: viewExtent
          })
        }

        this.map.on('moveend', onMoveEnd)
        this.map.on('movestart', onMoveStart)

        if (this.staticMap) {
          this.map.getInteractions().forEach(function (interaction) {
            interaction.setActive(false)
          })
        }
        resolve('setup map complete')
      })
    },

    visibleFeaturesInExtent: function (viewExtent = this.map.getView().calculateExtent()) {
      // Get a list of visible markers (pins)
      if (this.hasMarkerData && this.noSearchQuery) {
        let visibleFeatures = []
        this.markerLayer.getSource().forEachFeatureInExtent(viewExtent, function (feature) {
          _.forEach(feature.get('features'), feat => {
            visibleFeatures = [...visibleFeatures, feat]
          })
        })

        Bus.$emit('visibleFeatures', visibleFeatures)
      }
    },

    getExtentIfFinite: function (extent) {
      return !!extent && isFinite(extent[0]) && extent
    },

    fitMapToExtent: function (extent = null, duration = 250, minResolution = 0.3) {
      this.$nextTick(function () {
        // const mExtent = this.staticMap
        //   ? getExtentIfFinite(this.currentLocationLayer.getSource().getExtent())
        //   : extent || getExtentIfFinite(this.currentExtent) || this.autoExtent
        const mExtent = extent || this.getExtentIfFinite(this.currentExtent) || this.autoExtent

        if (!mExtent) {
          return
        }

        this.map.getView().fit(mExtent, {
          padding: [100, 100, 100, 100],
          minResolution,
          duration
        })
      })
    },

    handleNearMe: function (olFeature) {
      const feature = new GeoJSON().writeFeatureObject(olFeature)
      const featuresInContent = this.markerLayer.getSource().getSource().getFeatures()
      const points = turf.featureCollection(_.map(featuresInContent, f => turf.toWgs84(new GeoJSON().writeFeatureObject(f))))
      const mPoint = turf.toWgs84(feature)
      const nearest = turf.nearestPoint(mPoint, points)
      const distance = turf.distance(nearest, mPoint, { units: 'meters' })
      const poitnExtent = olFeature.getGeometry().getExtent()
      const bufferedExtent = olExtent.buffer(poitnExtent, distance < 5000 ? distance * 4 : distance * 1.2)
      this.fitMapToExtent(bufferedExtent, 1000)
    },

    setupPopup: function () {
      const _vm = this
      const container = document.getElementById(`popup-${this.cMapId}`)
      const closer = document.getElementById(`popup-closer-${this.cMapId}`)

      const overlay = new Overlay({
        id: `popup-${this.cMapId}`,
        element: container,
        autoPan: false,
        autoPanAnimation: {
          duration: 250
        }
      })
      this.map.addOverlay(overlay)

      const byggnadHasPoint = (byggnadExtent, mFeature) => {
        const featuresInContent = this.markerLayer.getSource().getSource().getFeaturesInExtent(byggnadExtent)
        const featureInContent = !_.isEmpty(featuresInContent) &&
              new GeoJSON().writeFeatureObject(featuresInContent[0])
        return _.includes(mFeature.id, 'view_kulturarv_byggnad') &&
              featureInContent &&
              turf.booleanWithin(featureInContent, mFeature) &&
              this.markerLayer.getSource().getSource().getFeaturesInExtent(byggnadExtent)[0]
      }

      const showPopup = (feature, coordinate) => {
        if (isCluster(feature)) {
          const extent = feature.get('features')[0].getGeometry().getExtent()
          _.each(feature.get('features'), feat => {
            olExtent.extend(extent, feat.getGeometry().getExtent())
          })
          this.fitMapToExtent(extent, 1000, 0.1)
        } else {
          feature = !feature.get('features') ? feature : feature.get('features')[0]
          const mFeature = new GeoJSON().writeFeatureObject(feature)
          coordinate = coordinate || feature.getGeometry().getExtent()
          if (_.get(mFeature, 'geometry.type') === 'Point' && !mFeature.properties.currentLocation) { // Point but not currentLocation
            this.popup = {
              feature,
              title: feature.values_.titel,
              link: feature.values_.resource_url
            }
            overlay.setPosition(coordinate)
          }
          if (_.get(mFeature, 'properties.category') === 'boundary') {
            this.popup = {
              feature,
              title: mFeature.properties.display_name,
              extent: feature.getGeometry().getExtent()
            }
            overlay.setPosition(getCoord(turf.centerOfMass(mFeature)))
          }
          // Kulturarv
          if (_.includes(mFeature.id, 'view_kulturarv')) {
            const containedFeature = byggnadHasPoint(feature.getGeometry().getExtent(), mFeature, feature)
            if (containedFeature) {
              return showPopup(containedFeature)
            }
            // console.log(mFeature)
            var title = _.includes(mFeature.id, 'view_kulturarv_omraden')
              ? 'Särskilt kulturhistoriskt värdefullt bebyggelseområde'
              : mFeature.properties.namn || 'Särskilt kulturhistoriskt värdefull byggnad'

            if (_.includes(mFeature.id, 'sala')) {
              title = _.includes(mFeature.id, 'view_kulturarv_omraden')
                ? 'Inventerat bebyggelseområde'
                : mFeature.properties.namn || 'Inventerad byggnad'
            }
            this.popup = {
              feature,
              title: title,
              subtitle: mFeature.properties.namn,
              // modal: _.includes(mFeature.id, 'view_kulturarv_omraden') ? mFeature : null,
              link: getUrl(`/plats/byggnadsinventering/${mFeature.properties.byggnadsid}`),
              extent: feature.getGeometry().getExtent()
            }

            overlay.setPosition(getCoord(turf.centerOfMass(mFeature)))
          }
        }
      }

      this.map.on('singleclick', function (event) {
        _vm.focused = true
        if (this.hasFeatureAtPixel(event.pixel) === true) {
          const features = this.getFeaturesAtPixel(event.pixel)
          const feature = _.find(features, f => {
            return f.getId() !== 'accuracyFeature'
          })
          if (_.get(new GeoJSON().writeFeatureObject(feature), 'properties.type') === 'position') {
            _vm.handleNearMe(feature)
          } else {
            showPopup(feature, feature.getGeometry().getCoordinates())
          }
        } else {
          overlay.setPosition(undefined)
          closer.blur()
        }
      })

      Bus.$on('focusLocation', extent => {
        const feature = this.markerLayer.getSource().getSource().getFeaturesInExtent(extent)[0]
        showPopup(feature, feature.getGeometry().getCoordinates())
        // this.fitMapToExtent(feature.getGeometry().getExtent())
        // this.map.getView().setCenter(feature.getGeometry().getCoordinates())
        this.map.getView().fit(feature.getGeometry().getExtent(), {
          padding: [100, 100, 100, 100],
          minResolution: this.map.getView().getResolution(),
          duration: 250
        })
      })

      Bus.$on('hoverLocation', (extent, out) => {
        const hoveredFeature = this.markerLayer.getSource().getSource().getFeaturesInExtent(extent)[0]

        const _this = this
        this.markerLayer.setStyle(function (feature) {
          const featureSum = Math.abs(feature.getGeometry().getCoordinates()[0] + feature.getGeometry().getCoordinates()[1])
          const hoveredSum = Math.abs(hoveredFeature.getGeometry().getCoordinates()[0] + hoveredFeature.getGeometry().getCoordinates()[1])
          const size = feature.get('features').length
          const style = _this.markerLayerStyleCache[size]
          if (featureSum === hoveredSum && !out) {
            return _this.markerLayerStyleCache.hovered
          }

          return style
        })
      })
    },

    hidePopup: function () {
      const overlay = this.map.getOverlayById(`popup-${this.cMapId}`)
      overlay && overlay.setPosition(undefined)
    }
  },

  computed: {
    savedViewExtent () {
      return this.$store.getters.savedViewExtentForType(this.type)
    },
    cMapId: function () {
      return this.mapId || 'mapid'
    },
    singleMarker: function () {
      return _.size(this.currentLocationLayer.getSource().getFeatures()) === 1
    },
    byggnadsInvByggnadsExtent: function () {
      const byggnadsInvByggnadLayers = this.byggnadsInvLayers.filter(layer => {
        return _.includes(layer.get('name'), 'byggnader')
      })
      const byggnadsInvFeatures = _.flatten(_.map(byggnadsInvByggnadLayers, layer => {
        return layer.getSource().getSource().getFeatures()
      }))
      const byggnadsInvExtent = _.size(byggnadsInvFeatures) > 0
        ? olExtent.boundingExtent(_.map(byggnadsInvFeatures, feature => {
          return feature.getGeometry().getExtent()
        }))
        : null

      return byggnadsInvExtent
    },
    autoExtent: function () {
      return isFinite(this.markerLayer.getSource().getSource().getExtent()[0])
        ? this.markerLayer.getSource().getSource().getExtent()
        : isFinite(this.currentLocationLayer.getSource().getExtent()[0])
          ? this.currentLocationLayer.getSource().getExtent()

          : isFinite(this.vastmanlandLayer.getSource().getExtent()[0])
            ? this.vastmanlandLayer.getSource().getExtent()
            : null
    },
    hasMarkerData: function () {
      return _.size(this.markerLayer.getSource().getSource().getFeatures()) > 0 ||
            _.size(_.get(this.currentLocation, 'features')) > 0
    },
    ...mapState({
      searchQuery: state => state.search.searchQuery,
      searchResults: state => state.search.searchResults,
      isScrolling: state => state.ui.isScrolling,
      activeMapOnLocation: state => state.ui.activeMapOnLocation,
      showByggnadsInventering: state => state.search.showByggnadsinventering
    }),
    noSearchQuery: function () {
      return !this.searchQuery || this.searchQuery === ''
    }
  },

  watch: {
    showByggnadsInventering: function (value) {
      this.setupKulturarv()
    },
    geoJson: function (newData, oldData) {
      if (newData && newData !== oldData) {
        this.handleMarkerLayer(newData)
      }

      setTimeout(() => {
        // combine extent from this.markerLayer and this.byggnadsInvByggnadsExtent
        const markerLayerExtent = this.markerLayer && this.markerLayer.getSource().getSource().getExtent()
        const byggnadsInvExtent = this.byggnadsInvByggnadsExtent
        const infiniteMarkerLayerExtent = isFinite(markerLayerExtent[0])
        const infiniteByggnadsInvExtent = byggnadsInvExtent && isFinite(byggnadsInvExtent[0])

        // const combinedExtent = olExtent.createEmpty()
        // if (infiniteMarkerLayerExtent) {
        //   console.log('extending markerLayerExtent', markerLayerExtent)
        //   olExtent.extend(combinedExtent, markerLayerExtent)
        // }

        // if (infiniteByggnadsInvExtent) {
        //   console.log('extending byggnadsInvExtent', byggnadsInvExtent)
        //   olExtent.extend(combinedExtent, byggnadsInvExtent)
        // }

        this.currentExtent = (newData && !this.savedViewExtent)
          ? infiniteMarkerLayerExtent
            ? markerLayerExtent
            : infiniteByggnadsInvExtent
              ? byggnadsInvExtent
              : null
          : null
      }, 1000)
    },
    searchResults: function (newData, oldData) {
      if (!this.geoJson && !!_.get(newData, 'locations.result')) {
        this.handleMarkerLayer(setupGeoJson(_.get(newData, 'locations.result'), newData.addressLocations))
      }

      if (newData.matches !== oldData.matches && !!_.get(newData, 'byggnadsInventering.result')) {
        this.byggnadsInvLayers.forEach(layer => {
          this.map.removeLayer(layer)
        })

        this.byggnadsInvLayers = []
        // this.byggnadsInvIds = this.searchQuery ? _.map(_.get(newData, 'byggnadsInventering.result'), 'id') : []

        this.setupKulturarv()
      }
    },
    currentLocation: function (newData, oldData) {
      if (newData !== oldData) {
        this.currentLocationLayer.getSource().clear()
        this.currentLocationLayer.getSource().addFeatures(setupMarkers(newData))
      }
      // this.currentLocationLayer.setVisible(_.size(newData.features) > 0)
      // this.vastmanlandLayer.setVisible(_.size(newData.features) === 0 && this.map.getView().getZoom() < 10)
      this.currentExtent = _.size(newData.features) > 0
        ? this.currentLocationLayer.getSource().getExtent()
        : null
    },
    inactiveMap: function () {
      this.map && this.refreshMap()
    },
    currentExtent: function (newData, oldData) {
      newData && this.refreshMap()
    },
    isScrolling: function () {
      if (this.focused) {
        // this.focused = false
      }
    },
    focused: function (newValue, oldValue) {
      if (newValue !== oldValue && this.staticMap) {
        this.$store.dispatch('setActiveMapOnLocation', newValue)
        // this.map.interactions = newValue ? defaultInteractions({
        //   onFocusOnly: true
        // }) : null

        this.map && this.map.getInteractions().forEach(function (interaction) {
          interaction.setActive(newValue)
        })
      }
    },
    activeMapOnLocation: function (newValue) {
      if (this.staticMap) {
        if (newValue !== this.focused) {
          this.focused = newValue
        }

        this.map && this.map.getInteractions().forEach(function (interaction) {
          interaction.setActive(newValue)
        })
      }
    }
  }
}
</script>
